import classNames from 'classnames';
import React, { ReactNode } from 'react';

type MarkIconType = 'triangle' | 'arc';

interface GizmoProps {
  icon: ReactNode;
  wrapperClassType?:
    | 'default'
    | 'noLimit'
    | 'tool'
    | 'playControl'
    | 'header'
    | 'hoverIcon'
    | 'adsorb';
  interactable?: boolean;
  highlight?: boolean;
  markIcon?: boolean | MarkIconType;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: () => void;
}

function TriangleMarkIcon(props: { size?: number; className: string }) {
  const { className, size = 16 } = props;
  return (
    <span className={classNames(className, 'inline-flex')} style={{ fontSize: `${size}px` }}>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        version='1.1'
        viewBox='0 0 24 24'
        width='1em'
        height='1em'
      >
        <polygon points='24,24 24,0 0,24' fill='#B4B7C1' />
      </svg>
    </span>
  );
}
function ArcMarkIcon(props: { size?: number; className: string }) {
  const { className, size = 16 } = props;
  return (
    <span className={classNames(className, 'inline-flex')} style={{ fontSize: `${size}px` }}>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='1em'
        height='1em'
        viewBox='0 0 4 4'
        fill='none'
      >
        <path d='M3 1V1C3 2.10457 2.10457 3 1 3V3' stroke='#B4B7C1' strokeLinecap='round' />
      </svg>
    </span>
  );
}

export function Gizmo(props: GizmoProps) {
  const {
    icon,
    highlight,
    interactable = true,
    markIcon = false,
    wrapperClassType = 'default',
    onClick,
    onMouseLeave,
  } = props;

  const getMarkIcon: () => ReactNode = () => {
    switch (markIcon) {
      case true:
      case 'triangle':
        return <TriangleMarkIcon className='absolute bottom-[1px] right-[1px]' size={4} />;
      case 'arc':
        return <ArcMarkIcon className='absolute bottom-[1px] right-[1px]' size={6} />;
      default:
        return null;
    }
  };

  const getWarpperClassName: () => Partial<{
    always: string;
    interactable: string;
    highlight: string;
    disabled: string;
    disabledClassName?: string;
  }> = () => {
    switch (wrapperClassType) {
      case 'default':
        return {
          always: 'p-1.5 border rounded-md border-FFFFFF1F h-7 w-7',
          interactable:
            'bg-bgColor17171A border-FFFFFF0F hover:border-primary hover:text-white hover:bg-primary',
        };

      case 'noLimit':
        return {
          always: 'px-2 rounded h-6',
          interactable: 'hover:text-white hover:bg-primary',
        };
      case 'tool':
        return {
          always: 'h-6 w-6 rounded-md',
          interactable: 'bg-bgColor17171A hover:border-primary hover:text-white hover:bg-primary',
          disabledClassName: 'cursor-not-allowed text-content4',
        };
      case 'playControl':
        return {
          always: 'p-1.5 border rounded border-FFFFFF1F h-7 w-7 text-white',
          interactable:
            'bg-FFFFFF0A border-borderColorMain hover:border-primary hover:text-white hover:bg-primary',
        };

      case 'header': {
        return {
          always: 'h-6 w-6 rounded',
          interactable:
            'bg-FFFFFF14 border-borderColorMain hover:border-primary hover:text-white hover:bg-primary',
        };
      }

      case 'hoverIcon': {
        return {
          always: 'w-6 h-6 rounded',
          interactable: 'hover:bg-FFFFFF0A hover:text-white',
        };
      }

      case 'adsorb': {
        return {
          always: 'p-1.5 border rounded-md border-FFFFFF1F h-7 w-7',
          interactable:
            'bg-bgColor17171A border-FFFFFF0F hover:border-bg252528 hover:text-white hover:bg-primary',
        };
      }
    }
  };

  const classname = getWarpperClassName();

  return (
    <div
      className={classNames(
        'relative flex items-center justify-center',
        classname.always,
        interactable
          ? highlight
            ? 'cursor-pointer border-primary bg-primary text-white'
            : `cursor-pointer text-textColorBase ${classname.interactable}`
          : classname.disabledClassName ||
              'bg-textColorDisabled border-textColorDisabled text-textColorBase cursor-not-allowed',
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (interactable) {
          onClick?.(e);
        }
      }}
      onMouseLeave={onMouseLeave}
    >
      {icon}
      {getMarkIcon()}
    </div>
  );
}
